/* The month grid — STORY-014.
 *
 * ── the first day of the week is locale DATA, never a constant ──────────────
 * Monday in much of the world, Sunday in much of the rest, Saturday in some.
 * A hardcoded 1 is wrong for a third of the planet and looks perfectly normal
 * to whoever wrote it. `Intl.Locale.prototype.getWeekInfo` knows; where a
 * runtime does not implement it, the fallback is stated rather than assumed.
 *
 * ── every date here is a calendar day, and never an instant ─────────────────
 * `YYYY-MM-DD` strings throughout, built with `Date.UTC` and read back with
 * `getUTC*`. No local-time method appears anywhere in this file, which is what
 * `lang-typescript.md` bans and what makes the whole thing zone-independent:
 * a grid of days has no timezone, and giving it one is the bug.
 *
 * The Venue's-zone requirement in the story is satisfied by the STORAGE model,
 * not by conversion here — an Event's date IS the calendar day at its Venue, so
 * grouping by it already groups in that zone. Converting an instant back into a
 * day is the mistake the obvious implementation makes, and this file cannot
 * make it because it never sees an instant.
 */
/**
 * Monday, as the fallback when a runtime cannot say.
 *
 * Named rather than inlined so it reads as a deliberate default and not as the
 * assumption this file exists to avoid. ISO-8601 says Monday, which makes it
 * the least surprising wrong answer.
 */
export const FALLBACK_FIRST_WEEKDAY = 1;
/**
 * Which weekday a locale starts its week on.
 *
 * `getWeekInfo` is a method on newer runtimes and a property on some older
 * ones, and absent on others — hence the shape check rather than a version
 * assumption. Its `firstDay` is 1–7 with Monday as 1 and **Sunday as 7**, which
 * is not the same numbering as `getUTCDay`; the modulo converts it.
 */
export function firstWeekdayOf(locale) {
    try {
        const info = new Intl.Locale(locale);
        const firstDay = info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay;
        if (typeof firstDay === 'number')
            return (firstDay % 7);
    }
    catch {
        /* A malformed locale tag. Falling through to the stated default is better
         * than throwing on a screen whose job is to render a month. */
    }
    return FALLBACK_FIRST_WEEKDAY;
}
const pad = (value) => String(value).padStart(2, '0');
/** `YYYY-MM-DD` from UTC parts. The only place a date string is built. */
export const toDateString = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
/**
 * Every day in the grid for one month, spanning complete weeks.
 *
 * Leading and trailing cells belong to the adjacent months and say so, because
 * a grid that starts mid-row is harder to read than one with a few greyed
 * cells — and a cell that looks like it belongs to this month and does not is
 * worse than either.
 *
 * @param month `YYYY-MM`.
 */
export function monthGrid(month, firstWeekday) {
    const [year, monthNumber] = month.split('-').map(Number);
    if (year === undefined || monthNumber === undefined)
        return [];
    /* `Date.UTC` maps years 0–99 to 1900–1999, so `0050-03` would silently
     * produce a grid of March 1950 while every label still said 0050. Refused
     * rather than remapped: it is only reachable by hand-editing a URL, and
     * failing loudly beats answering a question nobody asked. */
    if (year < 100)
        return [];
    const first = new Date(Date.UTC(year, monthNumber - 1, 1));
    /* Day 0 of the NEXT month is the last day of this one — which is how
     * February gets 29 days in a leap year without this file knowing the rule. */
    const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
    /* How many cells before the 1st. `+ 7` before the modulo because the
     * subtraction goes negative for any month starting before the locale's first
     * weekday, and a negative modulo in JavaScript stays negative. */
    const lead = (first.getUTCDay() - firstWeekday + 7) % 7;
    const days = [];
    for (let index = 0; index < lead; index += 1) {
        days.push({
            date: toDateString(new Date(Date.UTC(year, monthNumber - 1, 1 - (lead - index)))),
            inMonth: false,
        });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        days.push({
            date: toDateString(new Date(Date.UTC(year, monthNumber - 1, day))),
            inMonth: true,
        });
    }
    /* Complete the last week. Never a fixed six rows: a 28-day February starting
     * on the first weekday is exactly four, and padding it to six would add a
     * blank week nobody asked for. */
    const trail = (7 - (days.length % 7)) % 7;
    for (let index = 1; index <= trail; index += 1) {
        days.push({
            date: toDateString(new Date(Date.UTC(year, monthNumber, index))),
            inMonth: false,
        });
    }
    return days;
}
/** The month a date string belongs to, as `YYYY-MM`. */
export const monthOf = (date) => date.slice(0, 7);
/**
 * Shifting a month, without ever constructing an invalid date.
 *
 * `Date.UTC` normalises month 12 to January of the next year and month −1 to
 * December of the previous one, so the arithmetic needs no wrapping of its own.
 * Day 1 always exists, which is why this shifts months and not days — shifting
 * "31 January" by a month is a question with no good answer.
 */
export function shiftMonth(month, by) {
    const [year, monthNumber] = month.split('-').map(Number);
    if (year === undefined || monthNumber === undefined)
        return month;
    /* Same remapping, same refusal — shifting 0099-12 back once more produced
     * 1999-11, which is a different century arrived at silently. */
    if (year < 100)
        return month;
    const shifted = new Date(Date.UTC(year, monthNumber - 1 + by, 1));
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}`;
}
//# sourceMappingURL=month-grid.js.map